var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { React, jsx, css, classNames, hooks } from 'jimu-core';
import { Input as BSInput } from 'reactstrap';
import { LoadingType } from './types';
import { withStyles } from 'jimu-theme';
import { Loading } from './loading';
import defaultMessage from '../translations/default';
import { fetchTextInputValidityResult } from '../utils/utils';
import { WarningOutlined } from 'jimu-icons/outlined/suggested/warning';
/**
 * The `TextArea` input component is similar to the `TextInput` component, but creates a large input area and is intended to be used for longer texts, like messages.
 *
 * ```ts
 * import { TextArea } from 'jimu-ui'
 * ```
 */
export const _TextArea = React.forwardRef(function TextAreaComponent(props, ref) {
    const { onAcceptValue, value, className, style, height, readOnly, disabled, checkValidityOnAccept, checkValidityOnChange } = props, otherProps = __rest(props, ["onAcceptValue", "value", "className", "style", "height", "readOnly", "disabled", "checkValidityOnAccept", "checkValidityOnChange"]);
    const isControlled = React.useMemo(() => value != null, [value]);
    const [isValid, setIsValid] = React.useState(true); // by default input is valid
    const [isBusy, setIsBusy] = React.useState(false);
    const errorMsg = React.useRef();
    const cancelable = hooks.useCancelablePromiseMaker();
    const refObj = React.useRef();
    const handleRef = hooks.useForkRef(refObj, ref);
    const textHeight = React.useRef(0);
    const getI18nMessage = hooks.useTranslation(defaultMessage);
    // Only show validating result after user changes something.
    const shouldShowValidResult = React.useRef(false);
    React.useEffect(() => {
        let checkFunc;
        if (typeof checkValidityOnChange === 'function' && value) {
            checkFunc = checkValidityOnChange;
        }
        else if (typeof checkValidityOnAccept === 'function' && value) {
            checkFunc = checkValidityOnAccept;
        }
        if (checkFunc) {
            setIsBusy(true);
            textHeight.current = refObj.current.clientHeight;
            fetchTextInputValidityResult(value, checkFunc)
                .then(checkResult => {
                if (!checkResult.valid) {
                    errorMsg.current = checkResult.msg;
                    shouldShowValidResult.current = true;
                }
            })
                .finally(() => { setIsBusy(false); });
        }
    }, []); //eslint-disable-line react-hooks/exhaustive-deps
    const onChange = (evt) => {
        var _a;
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, evt);
        let checkResult;
        const value = evt.target.value;
        if (typeof checkValidityOnChange === 'function') { // custom validation
            checkResult = checkValidityOnChange(value);
        }
        else if (props.required) {
            checkResult = { valid: refObj.current.checkValidity(), msg: getI18nMessage('requiredField') };
        }
        if (checkResult != null) {
            errorMsg.current = checkResult.msg;
            shouldShowValidResult.current = true;
            setIsValid(checkResult.valid);
        }
    };
    const onAccept = (text) => __awaiter(this, void 0, void 0, function* () {
        textHeight.current = refObj.current.clientHeight;
        setIsBusy(true);
        let checkResult;
        if (typeof checkValidityOnAccept === 'function') {
            checkResult = yield cancelable(fetchTextInputValidityResult(text, checkValidityOnAccept));
        }
        else if (props.required) {
            checkResult = { valid: refObj.current.checkValidity(), msg: getI18nMessage('requiredField') };
        }
        if (checkResult != null) {
            errorMsg.current = checkResult.msg;
            shouldShowValidResult.current = true;
            setIsValid(checkResult.valid);
        }
        if (!checkResult || checkResult.valid) {
            onAcceptValue === null || onAcceptValue === void 0 ? void 0 : onAcceptValue(text);
        }
        setIsBusy(false);
    });
    const onBlur = (evt) => {
        var _a;
        if (isValid) {
            onAccept(evt.target.value).finally(() => {
                var _a;
                (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, evt);
            });
        }
        else {
            (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, evt);
        }
    };
    const onFocus = (evt) => {
        var _a;
        (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props, evt);
    };
    const classes = classNames('jimu-input-area w-100', className);
    const getStyle = () => {
        return css `
      position: relative;
      textarea { height: ${height > 0 ? height + 'px' : 'auto'}; }
      .jimu-loading {
        position: absolute;
        top: ${Math.max(0, textHeight.current - 24)}px;
        right: 12px;
        left: auto;
        bottom: auto;
        width: 16px;
        height: 16px;
      }
    `;
    };
    return (jsx("div", { className: classes, css: getStyle(), style: style },
        jsx(BSInput, Object.assign({}, otherProps, { className: 'w-100', type: 'textarea', size: props.htmlSize, value: isControlled ? value : undefined, onChange: onChange, onBlur: onBlur, valid: shouldShowValidResult.current && isValid, invalid: shouldShowValidResult.current && !isValid, onFocus: onFocus, innerRef: handleRef, readOnly: readOnly, disabled: disabled || isBusy })),
        shouldShowValidResult.current && !isValid && (jsx("div", { className: 'warn-icon d-flex align-items-center mt-2' },
            jsx(WarningOutlined, { color: 'var(--sys-color-error-main)', css: css `min-width: 16px;` }),
            jsx("span", { className: 'ml-2', css: css `color: var(--sys-color-error-main)` }, errorMsg.current))),
        isBusy && (jsx(Loading, { type: LoadingType.Donut, width: 16, height: 16 }))));
});
/**
 * The `TextArea` input component is similar to the `TextInput` component, but creates a large input area and is intended to be used for longer texts, like messages.
 *
 * ```ts
 * import { TextArea } from 'jimu-ui'
 * ```
 */
export const TextArea = withStyles(_TextArea, 'TextArea');
